<template>
  <table>
    <tr>
      <th>Name</th>
      <td><input v-model="formData.name" name="vm[name]" type="text" size="20" class="form-control string required"></td>
    </tr>

    <tr>
      <th>Memory</th>
      <td>
        <input
          v-model="formData.mem"
          name="vm[mem]"
          type="text"
          size="10"
          class="form-control string required"
          :disabled="!editableState"
        >
      </td>
    </tr>

    <VmFormControls
      v-bind="selectProps"
      :values="selectData"
      @update:vcpu="selectData.vcpu = $event"
      @update:bios="selectData.bios = $event"
      @update:boot="selectData.boot = $event"
      @update:hddtype="selectData.hddtype = $event"
      @update:diskImage="selectData.diskImage = $event"
      @update:os="selectData.os = $event"
      @update:consoleType="selectData.consoleType = $event"
      @update:iso="selectData.iso = $event"
      @update:owner="selectData.owner = $event"
    />
    <template v-if="showAdvanced">
      <tr>
        <th>NICs</th>
        <td v-if="editableState">
          <input id="vm_nics" name="vm[nics]" type="hidden" :value="nicCount">
          <div class="vm-nic-controls">
            <span id="vm-nic-count" class="badge badge-secondary">{{ nicCount }}</span>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="addNic">Add</button>
          </div>
        </td>
        <td v-else>
          <input name="vm[nics]" type="text" class="form-control string required" :value="nicCount" disabled>
        </td>
      </tr>

      <tbody id="vm-nic-fields">
        <template v-for="(nic, index) in nicRows" :key="`nic-${index}`">
          <tr
            class="vm-nic-group-heading vm-nic-group-row"
            :class="{ 'vm-nic-pending-remove': nic.pendingRemove }"
            :data-nic-index="nic.pendingRemove ? index : activeNicIndex(index)"
          >
            <td colspan="2">
              <div class="vm-nic-group-bar">
                <span class="vm-nic-chip">NIC {{ nic.pendingRemove ? nicDisplayLabel(nic, index) : activeNicIndex(index) + 1 }}</span>
                <span v-if="nic.pendingRemove" class="vm-nic-pending-copy">Will be removed on update</span>
                <button
                  v-if="editableState"
                  type="button"
                  class="vm-nic-inline-remove"
                  :class="{ 'is-undo': nic.pendingRemove }"
                  @click="nic.pendingRemove ? restoreNic(index) : removeNic(index)"
                >
                  {{ nic.pendingRemove ? "Undo" : "Remove" }}
                </button>
              </div>
            </td>
          </tr>
          <tr
            class="vm-nic-row vm-nic-row-start"
            :class="{ 'vm-nic-pending-remove': nic.pendingRemove }"
            :data-nic-index="nic.pendingRemove ? index : activeNicIndex(index)"
          >
            <th class="vm-nic-label" data-label-template="NIC Driver">NIC Driver</th>
            <td>
              <VmNicSelect
                :options="mediaOptions"
                :initial-value="nic.mediaId"
                :input-name="nic.pendingRemove ? '' : `vmif[${activeNicIndex(index)}][media_id]`"
                input-classes="vm-search-select-value"
                empty-label="Select a NIC driver"
                filter-placeholder="Filter NIC drivers"
                :readonly="!editableState || nic.pendingRemove"
              />
            </td>
          </tr>

          <tr
            class="vm-nic-row vm-nic-row-middle"
            :class="{ 'vm-nic-pending-remove': nic.pendingRemove }"
            :data-nic-index="nic.pendingRemove ? index : activeNicIndex(index)"
          >
            <th class="vm-nic-label" data-label-template="Network Type">Network Type</th>
            <td>
              <VmNicSelect
                :options="networkTypeOptions"
                :initial-value="nic.networkType"
                :input-name="nic.pendingRemove ? '' : `vmif[${activeNicIndex(index)}][network_type]`"
                input-classes="vm-search-select-value vm-nic-type-select"
                :input-data="{ 'data-vlan-target': `vlan_${nic.pendingRemove ? `pending_${index}` : activeNicIndex(index)}_network`, 'data-vxlan-target': `vxlan_${nic.pendingRemove ? `pending_${index}` : activeNicIndex(index)}_seg` }"
                empty-label="Select a network type"
                filter-placeholder="Filter network types"
                :readonly="!editableState || nic.pendingRemove"
                @update:value="handleNetworkTypeChange(index, $event)"
              />
            </td>
          </tr>

          <tr
            :id="`vlan_${nic.pendingRemove ? `pending_${index}` : activeNicIndex(index)}_network`"
            class="vm-nic-row vm-nic-row-middle"
            :class="{ 'vm-nic-pending-remove': nic.pendingRemove }"
            :data-nic-index="nic.pendingRemove ? index : activeNicIndex(index)"
            :style="{ display: nic.networkType === 'vlan' ? '' : 'none' }"
          >
            <th class="vm-nic-label" data-label-template="Network">Network</th>
            <td>
              <VmNicSelect
                :options="networkOptions"
                :initial-value="nic.networkId"
                :input-name="nic.pendingRemove ? '' : `vmif[${activeNicIndex(index)}][network_id]`"
                input-classes="vm-nic-network-select vm-search-select-value"
                empty-label="Select a network"
                filter-placeholder="Filter networks"
                :readonly="!editableState || nic.pendingRemove"
              />
            </td>
          </tr>

          <tr
            :id="`vxlan_${nic.pendingRemove ? `pending_${index}` : activeNicIndex(index)}_seg`"
            class="vm-nic-row vm-nic-row-end"
            :class="{ 'vm-nic-pending-remove': nic.pendingRemove }"
            :data-nic-index="nic.pendingRemove ? index : activeNicIndex(index)"
            :style="{ display: nic.networkType === 'vxlan' ? '' : 'none' }"
          >
            <th class="vm-nic-label" data-label-template="VXLAN Segment">VXLAN Segment</th>
            <td>
              <VmNicSelect
                :options="vxlanOptions"
                :initial-value="nic.segmentId"
                :input-name="nic.pendingRemove ? '' : `vmif[${activeNicIndex(index)}][segment_id]`"
                input-classes="vm-nic-segment-select vm-search-select-value"
                empty-label="Select a VXLAN segment"
                filter-placeholder="Filter VXLAN segments"
                :readonly="!editableState || nic.pendingRemove"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </template>

    <tr v-if="showTemplateToggle">
      <th>Template VM</th>
      <td>
        <input name="vm[state]" type="hidden" :value="templateChecked ? 1 : 0">
        <label class="vm-switch-toggle">
          <input v-model="templateChecked" type="checkbox">
          <span class="vm-switch-slider" aria-hidden="true"></span>
          <span class="vm-switch-copy">
            <strong>Registered as template</strong>
            <small>Save this VM as a reusable template instead of a regular stopped VM.</small>
          </span>
        </label>
      </td>
    </tr>

    <tr>
      <th>Extra Args</th>
      <td><input v-model="formData.extraArgs" name="vm[extra_args]" type="text" size="250" class="form-control string required"></td>
    </tr>

    <tr>
      <th>Notes</th>
      <td><input v-model="formData.comment" name="vm[comment]" type="text" size="40" class="form-control string required"></td>
    </tr>
  </table>
</template>

<script setup>
import { computed, reactive, ref } from "vue"
import VmFormControls from "./VmFormControls.vue"
import VmNicSelect from "./VmNicSelect.vue"

const props = defineProps({
  formData: {
    type: Object,
    default: () => ({})
  },
  selectProps: {
    type: Object,
    default: () => ({})
  },
  mediaOptions: {
    type: Array,
    default: () => []
  },
  networkTypeOptions: {
    type: Array,
    default: () => []
  },
  networkOptions: {
    type: Array,
    default: () => []
  },
  vxlanOptions: {
    type: Array,
    default: () => []
  },
  nics: {
    type: Array,
    default: () => []
  },
  editableState: {
    type: Boolean,
    default: false
  },
  showAdvanced: {
    type: Boolean,
    default: false
  },
  showTemplateToggle: {
    type: Boolean,
    default: false
  },
  initialTemplateChecked: {
    type: Boolean,
    default: false
  }
})

const formData = reactive({
  name: props.formData.name || "",
  mem: props.formData.mem || "",
  extraArgs: props.formData.extraArgs || "",
  comment: props.formData.comment || ""
})

const selectData = reactive({
  vcpu: props.selectProps.initialVcpu || "",
  bios: props.selectProps.initialBios || "",
  boot: props.selectProps.initialBoot || "",
  hddtype: props.selectProps.initialHddtype || "",
  diskImage: props.selectProps.initialDiskImage || "",
  consoleType: props.selectProps.initialConsoleType || "vnc",
  iso: props.selectProps.initialIso || "",
  owner: props.selectProps.initialOwner || ""
})

function nicDisplayLabel(nic, index) {
  return nic.displayLabel || activeNicIndex(index) + 1
}

const nicRows = reactive(
  (props.nics || []).map((nic) => ({
    displayLabel: nic.ifId || "",
    mediaId: nic.mediaId,
    networkType: nic.networkType || "vlan",
    networkId: nic.networkId,
    segmentId: nic.segmentId,
    pendingRemove: false
  }))
)

const templateChecked = ref(props.initialTemplateChecked)

const nicCount = computed(() => nicRows.filter((nic) => !nic.pendingRemove).length || 1)

function activeNicIndex(index) {
  return nicRows.slice(0, index + 1).filter((nic) => !nic.pendingRemove).length - 1
}

function addNic() {
  nicRows.push({
    displayLabel: "",
    mediaId: props.mediaOptions[0]?.value ?? "",
    networkType: "vlan",
    networkId: props.networkOptions[0]?.value ?? "",
    segmentId: props.vxlanOptions[0]?.value ?? "",
    pendingRemove: false
  })
}

function removeNic(index) {
  if (nicRows.filter((nic) => !nic.pendingRemove).length <= 1) {
    return
  }

  nicRows[index].pendingRemove = true
}

function restoreNic(index) {
  if (!nicRows[index]) {
    return
  }

  nicRows[index].pendingRemove = false
}

function handleNetworkTypeChange(index, value) {
  const nic = nicRows[index]

  if (!nic) {
    return
  }

  nic.networkType = value
  if (value === "vlan" && !nic.networkId) {
    nic.networkId = props.networkOptions[0]?.value ?? ""
  }
  if (value === "vxlan" && !nic.segmentId) {
    nic.segmentId = props.vxlanOptions[0]?.value ?? ""
  }
}
</script>
